import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { BRAND, SERVICES, TESTIDS } from "@/lib/site";
import SEO from "@/components/SEO";

const HERO_BG =
  "https://images.unsplash.com/photo-1710438399422-2fca27686bcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjB0ZXh0dXJlfGVufDB8fHx8MTc4MzQ3MjYwOHww&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Home() {
  return (
    <div>
      <SEO
        title=""
        path="/"
        description="Mobility-focused personal training and Fascial Stretch Therapy in Mississauga / GTA. Move better, feel stronger, build long-term joint health with BW MATRIX."
      />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/70 via-[#0A0A0C]/50 to-[#0A0A0C]" />
        <div className="absolute inset-0 bg-[#0A0A0C]/40" />
        <div className="relative max-w-7xl w-full mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-9">
            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="label-eyebrow mb-6"
            >
              {BRAND.location} · Mobility · FST · Strength
            </motion.p>
            <motion.h1
              variants={fadeUp}
              custom={1}
              initial="hidden"
              animate="show"
              className="font-display uppercase font-bold text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight text-white"
            >
              Move better.<br />
              Feel stronger.<br />
              <span className="text-white/50">Build joints</span> that last.
            </motion.h1>
            <motion.p
              variants={fadeUp}
              custom={2}
              initial="hidden"
              animate="show"
              className="mt-8 max-w-2xl text-base md:text-lg text-zinc-300 leading-relaxed"
            >
              Personal training and Fascial Stretch Therapy for people who
              want to move better, feel stronger, and build long-term joint health.
            </motion.p>
            <motion.div
              variants={fadeUp}
              custom={3}
              initial="hidden"
              animate="show"
              className="mt-10 flex flex-wrap items-center gap-4"
            >
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE STRIP */}
      <section className="border-b border-white/10 overflow-hidden bg-[#0A0A0C]">
        <div className="flex whitespace-nowrap animate-marquee py-6">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-12 pr-12 font-display uppercase text-2xl md:text-3xl font-bold tracking-tight text-white/80">
              <span>Fascial Stretch Therapy</span><span className="text-white/20">✦</span>
              <span>Mobility Assessment</span><span className="text-white/20">✦</span>
              <span>Personal Training</span><span className="text-white/20">✦</span>
              <span>Mobility + Strength</span><span className="text-white/20">✦</span>
              <span>Athletic Performance</span><span className="text-white/20">✦</span>
            </div>
          ))}
        </div>
      </section>

      {/* TAGLINE */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-5">
            <div className="overflow-hidden border border-white/10 aspect-[3/4] md:aspect-[4/5] w-full">
              <img
                src="/images/fst-session.png"
                alt="Brandon performing Fascial Stretch Therapy on a client"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="md:col-span-7">
            <p className="label-eyebrow mb-6">The Focus</p>
            <p className="font-display uppercase text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-white">
              {BRAND.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES TETRIS */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <p className="label-eyebrow mb-4">Services</p>
              <h2 className="font-display uppercase font-bold text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight text-white">
                What we work on<br />together.
              </h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-white hover:text-zinc-300 border-b border-white/40 pb-1">
              All services <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-min">
            {SERVICES.map((s, idx) => {
              // 6-card asymmetric grid → row 1: 7|5, row 2: 5|7, row 3: 6|6
              const span =
                idx === 0 ? "md:col-span-7" :
                idx === 1 ? "md:col-span-5" :
                idx === 2 ? "md:col-span-5" :
                idx === 3 ? "md:col-span-7" :
                idx === 4 ? "md:col-span-6" :
                "md:col-span-6";
              return (
                <Link
                  key={s.slug}
                  to="/services"
                  data-testid={TESTIDS.services.card(s.slug)}
                  className={`group relative bg-[#141417] border border-white/10 hover:border-white/40 transition-colors p-8 md:p-10 flex flex-col justify-between min-h-[220px] ${span}`}
                >
                  <div>
                    <p className="font-display text-white/30 text-lg font-bold">{s.number}</p>
                    <h3 className="mt-4 font-display uppercase text-2xl md:text-3xl font-bold text-white leading-tight tracking-tight">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-md">{s.short}</p>
                  </div>
                  <div className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-white">
                    Learn more
                    <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
